import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ConfigService } from '../../services/config.service';

export interface UserNotificationDto {
  NotificationId: number;
  Subject: string;
  MessageBody: string;
  IsRead: boolean;
  CreatedDate: Date;
  ReadDate?: Date;
  Priority?: 'Low' | 'Normal' | 'High' | 'Urgent';
  ObjectType?: string;
  ObjectId?: number;
  NavigationUrl?: string;
}

export interface NotificationPreferenceDto {
  PreferenceId?: number;
  InAppEnabled: boolean;
  EmailEnabled: boolean;
  SMSEnabled: boolean;
}

export interface NotificationCategoryConfigDto {
  category: string;
  label: string;
  description: string;
  requiredModuleId: number;
  allowInApp: boolean;
  allowEmail: boolean;
  allowSMS: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationCenterService {
  private get apiUrl(): string {
    return `${this.configService.get('apiBaseUrl')}/UserNotification`;
  }
  
  // Observable for real-time notification updates
  private notificationReceived = new Subject<UserNotificationDto>();
  public notificationReceived$ = this.notificationReceived.asObservable();

  private unreadCountChanged = new Subject<number>();
  public unreadCountChanged$ = this.unreadCountChanged.asObservable();

  constructor(private http: HttpClient, private configService: ConfigService) {}

  // Get user's notifications with pagination
  getNotifications(
    skip: number = 0,
    take: number = 20,
    includeRead: boolean = false
  ): Observable<{ notifications: UserNotificationDto[], total: number }> {
    let params = new HttpParams()
      .set('skip', skip.toString())
      .set('take', take.toString())
      .set('includeRead', includeRead.toString());

    return this.http.get<any>(
      `${this.apiUrl}/GetNotifications`,
      { params }
    ).pipe(
      map(response => {
        // Handle ApiResponse wrapper from backend
        if (response?.success && response?.result) {
          return {
            notifications: this.mapNotifications(response.result.notifications || []),
            total: response.result.total || 0
          };
        }
        // Fallback for direct response
        return {
          notifications: this.mapNotifications(response?.notifications || []),
          total: response?.total || 0
        };
      })
    );
  }

  // Map API response (camelCase) to DTO (PascalCase)
  private mapNotifications(notifications: any[]): UserNotificationDto[] {
    return notifications.map(n => ({
      NotificationId: n.notificationID,
      Subject: n.title,
      MessageBody: n.message,
      IsRead: n.isRead,
      CreatedDate: n.createdDate,
      ReadDate: n.readDate,
      Priority: n.notificationType, // Map notificationType to Priority
      ObjectType: n.objectType,
      ObjectId: n.objectId,
      NavigationUrl: n.navigationUrl
    }));
  }

  // Get unread count
  getUnreadCount(): Observable<number> {
    return this.http.get<any>(`${this.apiUrl}/GetUnreadCount`).pipe(
      map(response => {
        // Handle ApiResponse wrapper
        if (response?.success && typeof response?.result === 'number') {
          return response.result;
        }
        // Fallback for direct number response
        return typeof response === 'number' ? response : 0;
      })
    );
  }

  // Mark notification as read
  markAsRead(notificationId: number): Observable<boolean> {
    return this.http.post<any>(`${this.apiUrl}/MarkAsRead/${notificationId}`, {}).pipe(
      map(response => response?.success || false),
      tap(() => this.refreshUnreadCount())
    );
  }

  // Mark all as read
  markAllAsRead(): Observable<boolean> {
    return this.http.post<any>(`${this.apiUrl}/MarkAllAsRead`, {}).pipe(
      map(response => response?.success || false),
      tap(() => this.refreshUnreadCount())
    );
  }

  // Delete notification
  deleteNotification(notificationId: number): Observable<boolean> {
    return this.http.delete<any>(`${this.apiUrl}/DeleteNotification/${notificationId}`).pipe(
      map(response => response?.success || false),
      tap(() => this.refreshUnreadCount())
    );
  }

  // Get user's notification preferences
  getPreferences(): Observable<NotificationPreferenceDto> {
    return this.http.get<any>(`${this.apiUrl}/GetPreferences`).pipe(
      map(response => {
        // Handle ApiResponse wrapper from backend
        const backendPrefs = response?.result || response;
        
        // Map backend property names (camelCase from ASP.NET Core JSON) to frontend property names
        return {
          PreferenceId: backendPrefs?.preferenceID,
          InAppEnabled: backendPrefs?.allowInApp ?? true,
          EmailEnabled: backendPrefs?.allowEmail ?? true,
          SMSEnabled: backendPrefs?.allowSMS ?? true
        } as NotificationPreferenceDto;
      })
    );
  }

  // Save user's notification preferences
  savePreferences(preferences: NotificationPreferenceDto): Observable<boolean> {
    // Map frontend property names to backend property names
    const backendPrefs = {
      PreferenceID: preferences.PreferenceId,
      AllowInApp: preferences.InAppEnabled,
      AllowEmail: preferences.EmailEnabled,
      AllowSMS: preferences.SMSEnabled
    };
    
    return this.http.post<any>(`${this.apiUrl}/SavePreferences`, backendPrefs).pipe(
      map(response => response?.success || response?.result || false)
    );
  }

  // Get per-category channel preferences
  getCategoryPreferences(): Observable<NotificationCategoryConfigDto[]> {
    return this.http.get<any>(`${this.apiUrl}/GetCategoryPreferences`).pipe(
      map(response => {
        const items: any[] = response?.result || [];
        return items.map(item => ({
          category:        item.category,
          label:           item.label,
          description:     item.description,
          requiredModuleId: item.requiredModuleId,
          allowInApp:      item.allowInApp,
          allowEmail:      item.allowEmail,
          allowSMS:        item.allowSMS
        } as NotificationCategoryConfigDto));
      })
    );
  }

  // Save per-category channel preferences
  saveCategoryPreferences(prefs: NotificationCategoryConfigDto[]): Observable<boolean> {
    // Backend expects PascalCase properties matching NotificationCategoryConfig
    const body = prefs.map(p => ({
      Category:   p.category,
      AllowInApp:  p.allowInApp,
      AllowEmail:  p.allowEmail,
      AllowSMS:    p.allowSMS
    }));
    return this.http.post<any>(`${this.apiUrl}/SaveCategoryPreferences`, body).pipe(
      map(response => response?.success || false)
    );
  }

  // Emit new notification (called from SignalR or polling)
  emitNotification(notification: UserNotificationDto): void {
    this.notificationReceived.next(notification);
    this.refreshUnreadCount();
  }

  // Refresh unread count
  private refreshUnreadCount(): void {
    this.getUnreadCount().subscribe(count => {
      this.unreadCountChanged.next(count);
    });
  }

  /** Polling-handle reference. Holding it on the singleton means re-init from a re-entered
   *  component clears the previous interval first (the bell can mount/unmount across logout
   *  flows; without this each cycle stacked another timer). */
  private pollHandle: any = null;

  // Initialize - start polling or SignalR connection
  initialize(): void {
    this.refreshUnreadCount();
    // TODO: Setup SignalR connection for real-time notifications
    // For now, polling every 10 minutes
    if (this.pollHandle != null) {
      clearInterval(this.pollHandle);
    }
    this.pollHandle = setInterval(() => {
      this.refreshUnreadCount();
    }, 60000 * 10);
  }

  /** Stop the polling interval. Called from notification-bell.ngOnDestroy. */
  stopAutoRefresh(): void {
    if (this.pollHandle != null) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }
}
